import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Skeleton,
    SkeletonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { NumberByBreakpoint, sxAppend } from '@rhg/libs/material/sx';
import { getCustomLengthArray } from '@rhg/utils/arrays';
import { useIntl } from 'react-intl';

interface SkeletonGeneratorProps {
    count?: number;
}

export type SkeletonCardVariant =
    | 'clinical'
    | 'bannerV2'
    | 'daily'
    | 'discoverLargeButton'
    | 'horizontal'
    | 'quickNav';

export interface CardSizingProps {
    cardWidth?: number | string | NumberByBreakpoint;
    imageHeight?: string | number;
    imageWidth?: string | number;
    imageAspectRatio?: string;
    cardHeight?: string | number;
    contentHeight?: string | number;
}

interface CardSkeletonProps extends CardSizingProps {
    variant: SkeletonCardVariant;
    showAvatar?: boolean;
}
interface CarouselSkeletonProps
    extends SkeletonGeneratorProps,
        CardSizingProps {
    variant: SkeletonCardVariant;
    addContainerSpacing?: boolean;
    sx?: SxProps<Theme>;
}
interface ContributorSkeletonProps {
    itemWidth?: NumberByBreakpoint;
}
interface ContributorSkeletonCarouselProps
    extends SkeletonGeneratorProps,
        ContributorSkeletonProps {}

const stylePodSkeleton = {
    height: 200,
};

const styleCardSkeleton = {
    width: 400,
    height: 418,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: 'background.paper',
    color: 'grey.300',
    display: 'flex',
    flexDirection: 'column',
};
const styleCardSkeletonFooter = {
    p: 4,
    gap: 2,
    width: '100%',
};
const styleCardFooterText = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};
const styleCardFooterRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
};
const styleHorizontalFooterText = {
    mt: 3,
};
const styleEyebrowCardFooterRow = {
    ...styleCardFooterRow,
    gap: 2,
};
const styleSkeletonContainer = {
    display: 'grid',
    gap: 6,
    overflowX: 'hidden',
    maxWidth: 'fit-content',
    ml: 8,
};

const styleContributorSkeletonContainer = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr minmax(0, 1fr)',
    maxWidth: 'fit-content',
};
const styleContributorSkeleton = {
    height: 334,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
};
const styleContributorBio = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    width: '100%',
};
const styleContributorAvatar = {
    mb: 3,
};

export const SkeletonGenerator = ({
    count = 20,
    ...props
}: SkeletonGeneratorProps & SkeletonProps) => {
    const skeletons = getCustomLengthArray(count);
    return (
        <>
            {skeletons.map((_, index) => (
                <Skeleton key={index} {...props} />
            ))}
        </>
    );
};

export const HeadingSubHeadingSkeleton = ({ testId }: { testId?: string }) => (
    <div data-testid={testId}>
        <Skeleton variant="text" width="20%" height={32} />
        <Skeleton variant="text" height={32} />
    </div>
);

export const DiscoverQuickNavButton = ({
    cardWidth,
}: Omit<CardSizingProps, 'imageHeight' | 'cardHeight'>) => (
    <Box
        sx={sxAppend(styleCardSkeleton, {
            ...(cardWidth
                ? {
                      width: cardWidth,
                      height: 80,
                      flex: 1,
                      borderRadius: 3,
                  }
                : {}),
        })}
    >
        <Skeleton variant="rectangular" width="100%" height="100%" />
    </Box>
);

export const DiscoverQuickNavCarouselSkeleton = ({
    count = 6,
    cardWidth,
}: Omit<CarouselSkeletonProps, 'variant'>) => (
    <Box
        sx={sxAppend(styleSkeletonContainer, {
            my: 0,
            gridTemplateColumns:
                count > 3 ? `repeat(${count}, 1fr)` : '1fr 1fr minmax(0, 1fr)',
        })}
    >
        {getCustomLengthArray(count).map((_, index) => (
            <DiscoverQuickNavButton key={index} cardWidth={cardWidth} />
        ))}
    </Box>
);

export const CardSkeleton = ({
    variant = 'clinical',
    showAvatar = true,
    cardWidth,
    cardHeight,
    contentHeight,
    imageHeight = '266px',
    imageWidth = '100%',
    imageAspectRatio,
}: CardSkeletonProps) => {
    const { formatMessage } = useIntl();
    if (variant === 'discoverLargeButton') {
        return (
            <Box
                sx={sxAppend(styleCardSkeleton, {
                    ...(cardWidth
                        ? {
                              width: cardWidth,
                              height: 'auto',
                              flex: 1,
                              borderRadius: 3,
                          }
                        : {}),
                    ...(cardHeight ? { height: cardHeight } : {}),
                })}
            >
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={imageHeight}
                    sx={
                        imageAspectRatio
                            ? { aspectRatio: imageAspectRatio }
                            : {}
                    }
                />
                <Box sx={styleCardSkeletonFooter}>
                    <Box sx={styleCardFooterRow}>
                        <Box sx={styleCardFooterText}>
                            <Skeleton variant="text" height={33} width="70%" />
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    } else if (variant === 'bannerV2') {
        return (
            <Box
                sx={sxAppend(styleCardSkeleton, {
                    ...(cardWidth
                        ? {
                              width: cardWidth,
                              flex: 1,
                              borderRadius: 3,
                          }
                        : {}),
                })}
            >
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
        );
    }

    return (
        <Box
            sx={sxAppend(styleCardSkeleton, {
                ...(cardWidth ? { width: cardWidth } : {}),
                ...(cardHeight ? { height: cardHeight } : {}),
                ...(variant === 'horizontal' ? { flexDirection: 'row' } : {}),
            })}
        >
            <Skeleton
                variant="rectangular"
                height={imageHeight}
                width={imageWidth}
                role="progressbar"
                aria-label={formatMessage(commonMessages['common.loading'])}
                sx={{
                    ...(imageAspectRatio
                        ? { aspectRatio: imageAspectRatio }
                        : {}),
                }}
            />
            <Box
                sx={sxAppend(
                    styleCardSkeletonFooter,
                    variant === 'horizontal' && { width: 'auto', flex: 1 },
                    contentHeight ? { height: contentHeight } : {}
                )}
            >
                <Box sx={styleEyebrowCardFooterRow}>
                    <Skeleton variant="text" height={14} width="25%" />
                    {variant === 'clinical' && (
                        <>
                            <Skeleton variant="circular" height={7} width={7} />
                            <Skeleton variant="text" height={14} width="20%" />
                        </>
                    )}
                </Box>
                <Box sx={styleCardFooterRow}>
                    {showAvatar && variant === 'clinical' && (
                        <Skeleton variant="circular" width={40} height={40} />
                    )}
                    <Box
                        sx={sxAppend(
                            styleCardFooterText,
                            variant === 'horizontal' &&
                                styleHorizontalFooterText
                        )}
                    >
                        <Skeleton variant="text" height={24} width="70%" />
                        {variant !== 'horizontal' && (
                            <Skeleton variant="text" height={14} width="20%" />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export const GridCarouselSkeleton = ({
    rows = 1,
    columns = 3,
    variant = 'clinical',
    sx,
    ...otherProps
}: CarouselSkeletonProps & { rows: number; columns: number }) => (
    <Box
        sx={sxAppend(
            {
                display: 'grid',
                gap: 6,
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, minmax(${otherProps.cardHeight}, 1fr))`,
            },
            sx
        )}
    >
        {getCustomLengthArray(columns * rows).map((_, index) => (
            <CardSkeleton
                variant={variant}
                key={`skeleton-${index}`}
                {...otherProps}
            />
        ))}
    </Box>
);

export const CarouselSkeleton = ({
    count = 3,
    variant = 'clinical',
    addContainerSpacing,
    sx,
    ...otherProps
}: CarouselSkeletonProps) => (
    <Box
        sx={sxAppend(
            styleSkeletonContainer,
            {
                my:
                    variant === 'discoverLargeButton' || addContainerSpacing
                        ? 2
                        : 0,
                gridTemplateColumns:
                    count > 3
                        ? `repeat(${count}, 1fr)`
                        : '1fr 1fr minmax(0, 1fr)',
            },
            sx
        )}
    >
        {getCustomLengthArray(count).map((_, index) => (
            <CardSkeleton key={index} variant={variant} {...otherProps} />
        ))}
    </Box>
);

export const ContributorSkeleton = ({
    itemWidth,
}: ContributorSkeletonProps) => {
    return (
        <Box sx={sxAppend(styleContributorSkeleton, { width: itemWidth })}>
            <Box sx={styleContributorBio}>
                <Skeleton
                    variant="circular"
                    height={100}
                    width={100}
                    sx={styleContributorAvatar}
                />
                <Skeleton variant="text" height={32} width="50%" />
                <Skeleton variant="text" height={28} width="60%" />
            </Box>
            <Skeleton variant="rounded" width="80%" height={48} />
        </Box>
    );
};

export const ContributorSkeletonCarousel = ({
    count = 3,
    itemWidth,
}: ContributorSkeletonCarouselProps) => (
    <Box sx={styleContributorSkeletonContainer}>
        {getCustomLengthArray(count).map((_, index) => (
            <ContributorSkeleton key={index} itemWidth={itemWidth} />
        ))}
    </Box>
);

export function PodSkeleton({ sx }: Pick<SkeletonProps, 'sx'>) {
    return <Skeleton variant="rounded" sx={sxAppend(stylePodSkeleton, sx)} />;
}
